/**
 * @file User display, sorting, active-state, and permission-scope helpers.
 */

import { state, appData } from '../../app/state/state.js';

/**
 * Gets the initials (first letter of first and last name part) for a given user ID.
 * @param {string | null} userId - The ID of the user.
 * @returns {string} The user's initials (e.g., "JD") or "??" if not found or name missing.
 */
export function getUserInitials(userId) {
    if (!userId) {return '??';} // Return ?? if no ID provided
    const users = appData?.users || [];
    const user = users.find(u => u.id === userId);
    if (!user || !user.name || typeof user.name !== 'string' || user.name.trim() === '') {return '??';} // More robust check

    const parts = user.name.trim().split(' ').filter(Boolean); // Filter empty strings resulting from multiple spaces
    if (parts.length === 0) {return '??';}
    if (parts.length === 1) {return parts[0].substring(0, 2).toUpperCase();} // First two letters if single name

    // First letter of first part + first letter of last part
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Returns whether a user record is inactive.
 * @param {object | null} user User record.
 * @returns {boolean} True when the user is inactive.
 */
export function isUserInactive(user) {
    return user?.isActive === false;
}

/**
 * Formats a user's full display label, including inactive state.
 * @param {object | null} user User record.
 * @returns {string} Display label.
 */
export function getUserFullDisplayName(user) {
    if (!user) {return "Unknown";}
    const baseName = user.name || user.email || user.id || "Unknown";
    return isUserInactive(user) ? `${baseName} (inactive)` : baseName;
}

/**
 * Resolves a user ID into a display label.
 * @param {string | null} userId User document ID.
 * @returns {string} User display label or fallback ID.
 */
export function getUserDisplayNameById(userId) {
    if (!userId) {return "Unknown";}
    const user = (appData?.users || []).find((entry) => entry.id === userId);
    return getUserFullDisplayName(user || { id: userId, name: userId });
}

/**
 * Filters a user list to active users.
 * @param {Array<object>} users User records.
 * @returns {Array<object>} Active user records.
 */
export function getActiveUsers(users = []) {
    if (!Array.isArray(users)) {return [];}
    return users.filter((user) => !isUserInactive(user));
}

/**
 * Retrieves a sorted list of users from `userList`, placing the current logged-in user first if present.
 * Remaining users are sorted alphabetically by name.
 * @param {Array<object>} userList - The list of user objects (containing at least `id` and `name`).
 * @returns {Array<object>} The sorted list of users. Returns original or empty array if inputs invalid.
 */
export function getSortedUsers(userList) {
    // Basic validation
    if (!Array.isArray(userList) || userList.length === 0) {return userList || [];}
    const currentUserId = state?.currentUser?.uid;
    if (!currentUserId) { // If no current user, just sort alphabetically
        return [...userList].sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
    }

    const currentUser = userList.find(u => u.id === currentUserId);
    const otherUsers = userList.filter(u => u.id !== currentUserId);

    // Sort other users alphabetically by name (case-insensitive)
    otherUsers.sort((a, b) => {
        const nameA = a?.name?.toLowerCase() || ''; // Use safe navigation and default
        const nameB = b?.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
    });

    // Return current user first (if found), then the sorted list of others
    return currentUser ? [currentUser, ...otherUsers] : otherUsers;
}

/**
 * Filters the global user list (`appData.users`) to include only users who have permission
 * for a specific collection name (e.g., 'patients'). Admins are always included.
 * @param {string | null} collectionName - The plural name of the collection (e.g., 'patients', 'projects', 'assets') or null/undefined to return all users.
 * @returns {Array<object>} A list of user objects who have the required permission (or all users).
 */
export function getUsersWithPermission(collectionName) {
    const allUsers = appData?.users || [];
    // If no specific collection name is provided, return all users
    if (!collectionName) {
        return allUsers;
    }

    return allUsers.filter(user =>
        // Admins are always included
        user.role === 'admin' ||
        // Check if the user's permissions array includes the collection name
        (Array.isArray(user.permissions) && user.permissions.includes(collectionName))
    );
}

/**
 * Returns active users assignable for a collection permission scope.
 * @param {string | null} collectionName Collection permission key.
 * @returns {Array<object>} Active assignable users.
 */
export function getAssignableUsersWithPermission(collectionName) {
    return getActiveUsers(getUsersWithPermission(collectionName));
}

/**
 * Generates a minimal unique display name for a user (e.g. "Alex", "Alex S", or "Alex Smith").
 * Ensures uniqueness against the provided user list.
 * @param {object} user - The user object.
 * @param {Array<object>} allUsers - List of all users to check for collisions.
 * @returns {string} The unique display name.
 */
export function getUserDisplayName(user, allUsers) {
    if (!user || !user.name) {return "Unknown";}
    const firstName = user.name.split(' ')[0];

    // Check for other users with the same First Name
    const sameNameUsers = allUsers.filter(u =>
        u.id !== user.id &&
        u.name &&
        u.name.split(' ')[0].toLowerCase() === firstName.toLowerCase()
    );

    // If unique, return First Name
    if (sameNameUsers.length === 0) {
        return isUserInactive(user) ? `${firstName} (inactive)` : firstName;
    }

    // If collision, try First Name + Last Initial
    const lastInitial = user.name.split(' ').pop()[0] || '';
    const nameWithInitial = `${firstName} ${lastInitial}`;

    const sameInitialUsers = sameNameUsers.filter(u => {
        const uLast = u.name.split(' ').pop()[0] || '';
        return `${u.name.split(' ')[0]} ${uLast}` === nameWithInitial;
    });

    // If unique with initial, return that
    if (sameInitialUsers.length === 0) {
        return isUserInactive(user) ? `${nameWithInitial} (inactive)` : nameWithInitial;
    }

    // Fallback to Full Name
    return getUserFullDisplayName(user);
}
